import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { getCaptcha, login, setLoginInfo } from '../utils/api'
import './LoginPage.css'

function getDeviceId(): string {
  const cached = localStorage.getItem('cache_device_id')
  if (cached) return cached
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let id = ''
  for (let i = 0; i < 36; i++) {
    if ([8, 13, 18, 23].includes(i)) {
      id += '-'
    } else {
      id += chars[Math.floor(Math.random() * chars.length)]
    }
  }
  localStorage.setItem('cache_device_id', id)
  return id
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate()
  const [phone, setPhone] = useState('')
  const [otp, setOtp] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [sendingOtp, setSendingOtp] = useState(false)
  const [logging, setLogging] = useState(false)
  const [phoneError, setPhoneError] = useState('')
  const [otpError, setOtpError] = useState('')

  const normalizedPhone = phone
    .replace(/\D/g, '')
    .replace(/^62/, '')
    .replace(/^0/, '')
  const isPhoneValid = normalizedPhone.length > 0

  const maskedPhone = (() => {
    const p = phone.replace(/\D/g, '')
    if (p.length < 9) return phone
    return p.slice(0, 3) + '***' + p.slice(-3)
  })()

  const startCountdown = useCallback(() => {
    setCountdown(60)
  }, [])

  useEffect(() => {
    if (countdown <= 0) return
    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(timer); return 0 }
        return c - 1
      })
    }, 1000)
    return () => clearInterval(timer)
  }, [countdown])

  const handleSendOtp = async () => {
    console.log('[OTP] handleSendOtp called, phone:', phone, 'isPhoneValid:', isPhoneValid)
    if (!isPhoneValid) {
      setPhoneError('Nomor ponsel wajib diisi.')
      return
    }
    setSendingOtp(true)
    setPhoneError('')
    try {
      console.log('[OTP] calling getCaptcha...')
      const res = await getCaptcha({
        phone: normalizedPhone,
        device_id: getDeviceId(),
      })
      console.log('[OTP] getCaptcha res:', res)
      if (res?.code === 200) {
        setOtpSent(true)
        startCountdown()
      } else {
        setPhoneError(res?.message || 'Kode OTP gagal dikirim. Silakan coba lagi.')
      }
    } catch (err) {
      console.error('[OTP] error:', err)
      setPhoneError('Kode OTP gagal dikirim. Silakan coba lagi.')
    } finally {
      setSendingOtp(false)
    }
  }

  const handleLogin = async () => {
    if (!isPhoneValid) {
      setPhoneError('Nomor ponsel wajib diisi.')
      return
    }
    if (!otp) {
      setOtpError('Kode OTP wajib diisi.')
      return
    }
    setLogging(true)
    setOtpError('')
    try {
      const res = await login({
        phone: normalizedPhone,
        captcha: otp,
      })
      if (res?.code === 200 && res?.data) {
        const data = res.data as { token?: string; user_id?: string; user_name?: string }
        setLoginInfo(
          data?.token || '',
          data?.user_id || '',
          data?.user_name || phone,
        )
        navigate('/application', { replace: true })
      } else {
        setOtpError(res?.message || 'Kode OTP salah. Silakan coba lagi.')
      }
    } catch {
      setOtpError('Gagal masuk. Silakan coba lagi.')
    } finally {
      setLogging(false)
    }
  }

  return (
    <div className="login-root">
      {/* Header */}
      <header className="login-topbar">
        <button className="login-back-btn" onClick={() => navigate('/')}>
          ← Kembali
        </button>
        <span className="login-topbar-title">Pinjaman Kilat</span>
        <div style={{ width: 88 }} />
      </header>

      <div className="login-body">
        {/* Card */}
        <div className="login-card">
          <div className="login-card-head">
            <h2 className="login-card-title">Masuk ke Akun Anda</h2>
          </div>

          {/* Phone */}
          <div className="login-form-group">
            <label className="login-label">
              <span>📞</span> Nomor Ponsel
            </label>
            <div className="login-phone-row">
              <div className="login-country-box">
                <span>🇮🇩</span>
                <span>+62</span>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => { setPhone(e.target.value); setPhoneError('') }}
                placeholder="Masukkan nomor ponsel"
                maxLength={15}
                className="login-phone-input"
              />
            </div>
            {phoneError && <p className="login-field-error">⚠ {phoneError}</p>}
          </div>

          {/* OTP is always available so users can enter an existing code directly. */}
          <div className="login-form-group">
            <label className="login-label">
              <span>🔑</span> Kode OTP (4–6 digit)
            </label>
            <div className="login-otp-row">
              <input
                type="text"
                value={otp}
                onChange={(e) => {
                  setOtp(e.target.value.replace(/\D/g, ''))
                  setOtpError('')
                }}
                placeholder="Masukkan kode OTP"
                maxLength={6}
                className="login-otp-input"
                inputMode="numeric"
              />
              <button
                className="login-resend-btn"
                onClick={handleSendOtp}
                disabled={countdown > 0 || sendingOtp}
              >
                {sendingOtp ? 'Mengirim…' : countdown > 0 ? `${countdown}s` : otpSent ? 'Kirim Ulang' : 'Kirim OTP'}
              </button>
            </div>
            {otpSent && <p className="login-otp-hint">✉ Kode telah dikirim ke +62 {maskedPhone}</p>}
            {otpError && <p className="login-field-error">⚠ {otpError}</p>}
          </div>

          {/* Button */}
          <button
            className="login-primary-btn"
            onClick={handleLogin}
            disabled={logging}
          >
            {logging ? <span className="login-spinner" /> : 'Masuk ✓'}
          </button>

          <p className="login-terms-text">
            <a href="/terms" className="login-terms-link">Syarat dan Ketentuan</a>
            <span> · </span>
            <a href="/privacy" className="login-terms-link">Kebijakan Privasi</a>
          </p>
        </div>

        {/* Promo badges */}
        <div className="login-promo-row">
          <div className="login-promo-item"><span>⚡</span><span>Proses Cepat</span></div>
          <div className="login-promo-item"><span>💰</span><span>Dana Cair</span></div>
          <div className="login-promo-item"><span>🛡️</span><span>Aman</span></div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
